// @ts-strict-ignore
/**
 * @module Favorites
 */
import { Subject, Subscription, filter } from 'rxjs';
import xmlParser, { XMLElement } from 'rainbow-web-sdk/utils/xmlParser';
import { allSettled } from 'rainbow-web-sdk/utils/allSettled';

import { ErrorHelperInfo, ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Favorite, FavoriteRB, FavoriteType } from 'rainbow-web-sdk/models/favorite.model';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { ConversationRB, Conversation } from 'rainbow-web-sdk/models/conversation.model';
import { User } from 'rainbow-web-sdk/models';
import { Bubble, BubbleRB } from 'rainbow-web-sdk/models/bubble.model';

import { EventableService, makeServiceEvent, RbServiceEvent, Service } from 'rainbow-web-sdk/services/service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { ConversationServiceEvents, ConversationServiceRB, CONVERSATION_SVC } from 'rainbow-web-sdk/services/conversation/conversation.service';
import type { BubbleServiceRB } from 'rainbow-web-sdk/services/bubbles/bubble.service';
import { BUBBLE_SVC } from 'rainbow-web-sdk/services/bubbles/bubble.service';
import { CompanyDirectoryService } from 'rainbow-web-sdk/services/company/company-directory.service';
import { Office365AdService } from 'rainbow-web-sdk/services/directory/office365ad.service';
import { MaybeArray } from '../../typings/helpers';

/** @internal */
export const FAVORITES_SVC = 'FavoritesService';

/**
 * @eventProperty
 * Favorites service events send to other services
 * Can listen to it via `FavoritesService.subscribe()` API
 */
export enum FavoritesServiceEvents {

    /**
     * @eventProperty
     * This `RBEvent` is sent when a new favorite has been created.
     *
     * **Event Data**
     * - **favorite**: The new created favorite (as Favorite)
     */
    ON_FAVORITE_CREATED = "ON_FAVORITE_CREATED",

    /**
     * @eventProperty
     * This `RBEvent` is sent when a favorite has been deleted.
     *
     * **Event Data**
     * - **id**: The id (as string) of the favorite that has been deleted
     */
    ON_FAVORITE_DELETED = "ON_FAVORITE_DELETED",

    /**
     * @eventProperty
     * This `RBEvent` is sent when a favorite has been moved in the list.
     *
     * **Event Data**
     * - **favorite**: The favorite that has been moved in the list (as Favorite)
     * - **position**: The new position (starting from 0) of this favorite in the list
     */
    ON_FAVORITES_ORDER_CHANGED = "ON_FAVORITES_ORDER_CHANGED",

    /**
     * @internal
     * @eventProperty
     * This `RBEvent` is sent only when the favorite is related to a personal directory contact and this last has been updated.
     *
     * **Event Data**
     * - **favorite**: The updated favorite (as Favorite)
     */
    ON_FAVORITE_CONTACT_UPDATED = "ON_FAVORITE_CONTACT_UPDATED",
}

export interface IFavoritesServiceEvents {
    /**
     * @eventProperty
     * This `RBEvent` is sent when a new favorite has been created.
     */
    ON_FAVORITE_CREATED: {
        /**
         * The new created favorite (as Favorite)
         */
        favorite: Favorite;
    };

    /**
     * @eventProperty
     * This `RBEvent` is sent when a favorite has been deleted.
     */
    ON_FAVORITE_DELETED: {
        /**
         * The id (as string) of the favorite that has been deleted
         */
        id: string;
    };

    /**
     * @eventProperty
     * This `RBEvent` is sent when a favorite has been moved in the list.
     */
    ON_FAVORITES_ORDER_CHANGED: {
        /**
         * The favorite that has been moved in the list (as Favorite)
         */
        favorite: Favorite;
        /**
         * The new position (starting from 0) of this favorite in the list
         */
        position: number;
    };

    /**
     * @internal
     * @eventProperty
     * This `RBEvent` is sent only when the favorite is related to a personal directory contact and this last has been updated.
     */
    ON_FAVORITE_CONTACT_UPDATED: {
        /**
         * The updated favorite (as Favorite)
         */
        favorite: Favorite;
    };

    /**
     * @internal
     * @eventProperty
     * This `RBEvent` is sent only when the favorites are refreshed after a connection/reconnection
     */
    ON_FAVORITES_REFRESH: unknown;
}

export interface FavoritesService extends EventableService<IFavoritesServiceEvents> {

    /**
     * Subscribe to the favorites updates, all events are of RBEvent<FavoritesServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) array of event to listen
     * @returns Returns RxJS Subscription
     */
    subscribe(callback: (event: RBEvent<FavoritesServiceEvents>) => any, eventNames?: FavoritesServiceEvents | FavoritesServiceEvents[]): Subscription;

    /**
     * Get the complete list of my favorites with an array of Favorite.
     */
    getFavorites(): Favorite[];

    /**
     * Add the peer specified by its object to favorites list.
     * The item is specified by its object instance (User or Bubble) and must be one of the following: A user, a bubble, a bot,
     * a directory contact (from Personal Contacts or Business Contacts directories) or a Microsoft Azure AD user/Microsoft O365 contact.
     * A ON_FAVORITE_CREATED event is sent afterwards at the completion of the operation to notify about the favorite creation.
     * @param peerRef - Reference of the peer to add to favorites list.
     */
    addToFavorites(peerRef: User | Bubble): Promise<void>;

    /**
     * Delete the favorite specified by its identifier.
     * A ON_FAVORITE_DELETED event is sent afterwards at the completion of the operation to notify about the favorite deletion.
     * @param id - Favorite unique identifier.
     */
    deleteFavorite(id: string): Promise<void>;

    /**
     * Remove a peer specified by its object reference from favorites list.
     * A ON_FAVORITE_DELETED event is sent afterwards at the completion of the operation to notify about the favorite deletion.
     * @param peerRef - Reference of the peer to remove from favorites list.
     */
    deleteFromFavorites(peerRef: User | Bubble | null): Promise<void>;

    /**
     * Move a favorite in the favorites list.
     * The favorite specified by its object reference, is moved to the index specified in the parameter.
     * In addition, all existing favorites from this index will also have their positions automatically shifted by 1.
     * A ON_FAVORITES_ORDER_CHANGED event is sent afterwards at the completion of the operation to notify about the favorite's move.
     * @param favorite - Reference of the favorite to move in the favorites list.
     * @param position - The new position (starting from 0) of this favorite in the list.
     */
    moveFavoritePosition(favorite: Favorite, position: number): Promise<void>;
}

/**
 * @internal
 */
export class FavoritesServiceRB extends Service implements FavoritesService {

    private xmppManagementHandler: any = null;
    private conversationServiceSubscription: Subscription;

    // Observables for events sent by FavoritesService
    private readonly rxSubject = new Subject<RbServiceEvent<IFavoritesServiceEvents>>();

    // Services
    private xmppService: XmppService = XmppService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private companyDirectoryService: CompanyDirectoryService = CompanyDirectoryService.getInstance();
    private office365AdService: Office365AdService = Office365AdService.getInstance();

    // Injectable services, not always available
    private bubbleService?: BubbleServiceRB = undefined;
    private conversationService?: ConversationServiceRB = undefined;

    // List of Favorites (cache) - Directly used by UCAAS components
    public favorites: FavoriteRB[] = [];

    /**
     * Returns the Favorite service singleton
     */
    public static getInstance(): FavoritesServiceRB {
        return Service.create(FAVORITES_SVC, FavoritesServiceRB);
    }

    public static build(): FavoritesServiceRB {
        return new FavoritesServiceRB();
    }

    private constructor() {
        super();
    }

    // ------------------------------------------------------------
    //                      LIFE CYCLE STUFF
    // ------------------------------------------------------------

    public async start() {
        this.logger.info('[FavoritesService] === STARTING ===');

        try {
            // Dynamically inject services (should not be here)
            this.bubbleService = this.mainService.inject<BubbleServiceRB>(BUBBLE_SVC);
            this.conversationService = this.mainService.inject<ConversationServiceRB>(CONVERSATION_SVC);

            await this.connect();

            this.conversationServiceSubscription = this.conversationService.subscribe((event: RBEvent) => {
                try {
                    if (event.name as ConversationServiceEvents === ConversationServiceEvents.ON_SERVICE_STARTED) {
                        //listen to update of the conversation and reset all conv attached to the current favorites
                        this.updateConversationsForFavorites();
                    }
                    else if (event.name as ConversationServiceEvents === ConversationServiceEvents.ON_CONVERSATION_REMOVED) {
                        this.updateFavoriteConversations(event.data.conversation, true);
                    }
                    else if (event.name as ConversationServiceEvents === ConversationServiceEvents.ON_CONVERSATION_CREATED) {
                        this.updateFavoriteConversations(event.data.conversation, false);
                    }
                }
                catch (error) {
                    this.logger.debug(`[FavoritesService] on conversationService event error -- ${error}`);
                }
            });

            this.logger.info(`[FavoritesService] === STARTED ===`);
        }
        catch (error) {
            this.logger.error(`[FavoritesService] === STARTING FAILURE === ${error.message}`);
        }

    }

    public async connect() {
        try {
            // Get the list of favorites from Server
            await this.getFavoritesFromServer();
            this.attachHandlers();
            this.sendEvent('ON_FAVORITES_REFRESH', undefined);
        }
        catch (error) {
            this.logger.error(`[FavoritesService] connect -- Failure -- ${error.message}`);
        }
    }

    private attachHandlers() {
        this.removeHandlers();
        this.xmppManagementHandler = this.xmppService.addHandler((stanza) => { this.onManagementMessageReceived(stanza); return true; }, null, "message", "management");
    }

    private removeHandlers() {
        this.logger.debug(`[FavoritesService] removeHandlers `);
        // Remove all handlers
        if (this.xmppManagementHandler) {
            this.xmppService.deleteHandler(this.xmppManagementHandler);
            this.xmppManagementHandler = null;
        }
    }

    public async stop() {
        this.logger.info('[FavoritesService] === STOPPING ===');

        // Remove handlers and subscriptions
        this.removeHandlers();
        if (this.conversationServiceSubscription) {
            this.conversationServiceSubscription.unsubscribe();
            this.conversationServiceSubscription = null;
        }

        // Reset the  data
        this.favorites = [];

        this.logger.info('[FavoritesService] === STOPPED ===');
    }

    public async reconnect(): Promise<void> {
        this.logger.info('[FavoritesService] === RESTART ===');
        await this.stop();
        await this.start();
        this.logger.info('[FavoritesService] === RESTARTED ===');
    }

    /**
     * Subscribe to the favorites updates, all events are of RBEvent<FavoritesServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) array of event to listen
     * @returns Returns RxJS Subscription
     */
    public subscribe(callback: (event: RBEvent<FavoritesServiceEvents>) => any, eventNames?: MaybeArray<keyof IFavoritesServiceEvents>): Subscription {
        const observable = eventNames

            ? this.rxSubject.pipe(filter((event) => {
                if (eventNames instanceof Array) return eventNames.includes(event.name);

                return event.name === eventNames;
            }))

            : this.rxSubject;

        return observable.subscribe((event: RBEvent<FavoritesServiceEvents>) => {
            try { callback(event); }
            catch (error) { this.logger.error(`[FavoritesService] subscribe () -- error -- ${error.message}`); }
        });
    }

    /**
     * {@inheritdoc}
     */
    public events<EventNames extends keyof IFavoritesServiceEvents>(...eventNames: EventNames[]) {
        return makeServiceEvent(this.rxSubject, ...eventNames);
    }

    /**
     * @public
     * @param name - the name of the event
     * @param data - the data to be send (an object)
     * FavoritesService events send to other services via sendEvent()
     * Can listen to it via FavoritesServiceRB.subscribe() API
     */
    private sendEvent<const EventName extends keyof IFavoritesServiceEvents>(eventName: EventName, data: IFavoritesServiceEvents[EventName]): void {
        this.rxSubject.next(RBEvent.create(eventName, data));
    }

    // ------------------------------------------------------------
    //          EVENT LISTENERS ( Handling of events)
    // ------------------------------------------------------------

    /**
     * Management of ConversationServiceEvent.ON_SERVICE_STARTED event reception
     * Associate a conversation (if it exists) with all defined favorites.
     * If a conversation is found, it is also tagged as a favorite.
     */
    private updateConversationsForFavorites() {
        this.favorites.forEach((favorite: FavoriteRB) => {
            // Directory (Personal / Business directory contacts), O365 and Outlook favorites never have an associated conversation
            if (!this.isDirectoryFavorite(favorite.type)) {
                const convId: string = favorite.peer.jid;
                const conv: ConversationRB = this.conversationService.getConversationFromCache(convId);
                favorite.conv = conv;
                if (conv) { conv.isFavorite = true; }
            }
        });
    }

    /**
     * Management of a favorite when a conversation is created or deleted:
     * - Reception of ON_CONVERSATION_REMOVED event (conversation deleted)
     * - Explicit call (For example 'conversationServiceEventHandler' at conversation creation)
     * If a favorite associated to the given conversation is found
     *  - the conversation field associated to this favorite is updated (conv stored or reset)
     *  - the conversation is tagged as a favorite.
     * @param conversation - The created or deleted conversation
     * @param removed - Flag indicating whether the conversation is deleted (if false, it means it is created)
     */
    public updateFavoriteConversations(conversation: Conversation, removed: boolean = false): void {
        const peerId: string = conversation.user ? conversation.user.dbId : conversation.bubble.dbId;
        const favorite: FavoriteRB = this.favorites.find((favoriteConv: any) => { return favoriteConv.peerId === peerId; });
        if (favorite) {
            conversation.isFavorite = true;
            favorite.conv = (removed) ? null : conversation as ConversationRB;
        }
    }

    /**
     * Management of XMPP management message
     * @param stanza - The received stanza.
     */
    private async onManagementMessageReceived(stanza) {
        try {
            const stanzaElem: XMLElement = xmlParser(stanza);
            const favoriteElem = stanzaElem.find("favorite");

            if (favoriteElem) {
                const id: string = favoriteElem.attr("id");
                const type: string = favoriteElem.attr("type");
                const peerId: string = favoriteElem.attr("peer_id");
                const action: string = favoriteElem.attr("action");

                switch (action) {
                    // Notification about the creation of a favorite
                    case 'create':
                        try {
                            let favoriteRB: FavoriteRB = this.favorites.find((favoriteConv: any) => { return favoriteConv.peerId === peerId; });
                            // If the favorite has been found in internal cache, create it !
                            if (!favoriteRB) {
                                favoriteRB = await this.createFavorite(id, peerId, type as FavoriteType);
                                if (favoriteRB) {
                                    this.favorites.push(favoriteRB);
                                    this.logger.info(`[FavoritesService] onManagementMessageReceived -- create Favorite: id: ${id}, peer: ${peerId}, type: ${type}`);
                                    this.sendEvent(FavoritesServiceEvents.ON_FAVORITE_CREATED, { favorite: favoriteRB as Favorite });
                                }
                            }
                        }
                        catch (error) {
                            const errorInfo = await this.errorHelperService.getErrorInfo(error, "onManagementMessageReceived");
                            this.logger.error(`[FavoritesService] onManagementMessageReceived ${action} - failure: ${errorInfo.message}`);
                            throw errorInfo.error;
                        }
                        break;

                    // Notification about the deletion of a favorite
                    case 'delete':
                        {
                            const index: number = this.favorites.findIndex((fav) => { return fav.peerId === peerId; });
                            // If the favorite has been found in internal cache, remove it !
                            // Associated conversation is updated and no more tagged as favorite.
                            if (index !== -1) {
                                const favorite: FavoriteRB = this.favorites[index];
                                if (favorite.conv) { favorite.conv.isFavorite = false; }
                                this.favorites.splice(index, 1);
                                this.logger.info(`[FavoritesService] onManagementMessageReceived -- delete Favorite: id: ${favorite.id}`);
                                this.sendEvent(FavoritesServiceEvents.ON_FAVORITE_DELETED, { id: favorite.id });
                            }
                        }
                        break;

                    // Notification about the update of a favorite (change position in list, the new one is specified in message)
                    case 'update':
                        {
                            const targetIndex: number = parseInt(favoriteElem.attr('position'), 10);
                            const sourceIndex: number = this.favorites.findIndex((fav) => { return fav.id === id; });
                            if (sourceIndex !== -1) {
                                const updateFavorite: FavoriteRB = this.favorites[sourceIndex];
                                this.favorites.splice(targetIndex, 0, this.favorites.splice(sourceIndex, 1)[0]);
                                this.logger.info(`[FavoritesService] onManagementMessageReceived -- update Favorite: id (${id})`);
                                this.sendEvent(FavoritesServiceEvents.ON_FAVORITES_ORDER_CHANGED, { favorite: updateFavorite as Favorite, position: targetIndex });
                            }
                        }
                        break;

                        break;
                }
            }
            return true;
        }
        catch (error) {
            this.logger.error(`[FavoritesService] onManagementMessageReceived error: ${error}`);
            return true;
        }
    }

    // ------------------------------------------------------------
    //                      PRIVATE METHODS
    // ------------------------------------------------------------

    /**
     * Checks whether the favorite itype s associated with a directory contact such as ...
     * - Rainbow Personal or Business directory
     * - Microsoft Azure Active Directory
     * - Microsoft Outlook contacts
     * @param type - Type of favorite to check
     */
    private isDirectoryFavorite(type: FavoriteType): boolean {
        if ((type === FavoriteType.DIRECTORY) || (type === FavoriteType.OFFICE365) || (type === FavoriteType.OUTLOOK)) {
            return true;
        }
        return false;
    }

    /**
     * Get the list of favorites from the server.
     * Promise resolution with an array of all favorites.
     */
    private async getFavoritesFromServer(): Promise<Favorite[]> {
        try {
            const url: string = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/favorites`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) {
                this.logger.debug(`[FavoritesService] Request ${response.headers.get(`x-rainbow-request-id`)} failed with status ${response.status}`);
                throw new Error(response.statusText);
            }
            const responseData: any = await response.json();
            const favoriteRoomIds = responseData.data
                .filter((data: any) => data.type === FavoriteType.ROOM)
                .reduce((roomIds: string[], data: any) => {
                    if (!this.bubbleService.getBubbleFromCache(data.peerId)) roomIds.push(data.peerId);
                    return roomIds;
                }, []);

            // Get from server infos for the bubble not already present in cache
            try {
                await this.bubbleService.getServerBubblesByIds(favoriteRoomIds, false);
            } catch {
                this.logger.error(`[FavoritesService] getServerBubblesByIds -- failed -- no bubble favorite info`);
            }

            // Create all the favorites in  cache
            const promises = responseData.data.map(async (data: any) => { return this.createFavorite(data.id, data.peerId, data.type); });
            const favorites = await allSettled(promises);

            this.logger.info(`[FavoritesService] getFavoritesFromServer -- SUCCESS -- found ${favorites.length} favorites from server`);

            this.favorites = favorites.reduce<any[]>((favs: Favorite[], fav: any) => {
                if (fav.status === 'fulfilled' && fav.value) { favs.push(fav.value); }
                return favs;
            }, []);

            this.logger.info(`[FavoritesService] getFavoritesFromServer -- created ${this.favorites.length} favorites`);
            return this.favorites as Favorite[];
        }
        catch (error) {
            const errorMessage: string = `[FavoritesService] getFavoritesFromServer -- FAILURE -- ${error.message}`;
            this.logger.error(errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Add a peer specified by its id and type to favorites list
     * The item is specified by its Id and must be one of the following types: user, bubble, bot,
     * directory contact (from personal directory, business directory) and  O365 (Microsoft Azure) directory contact
     * @param peerId - Id of the peer marked as favorite
     * @param type - Type of favorite
     */
    private async addServerFavorite(peerId: string, type: FavoriteType): Promise<any> {
        try {
            // For Metric:  API .../users/:userId/favorites decorated with URL query parameters 'type=...'
            const url: string = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/favorites?type=${type}`;
            const headers = this.authService.getPostHeader();
            const body = { peerId, type };
            const response: Response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
            if (!response.ok) { throw response; }

            this.logger.info(`[FavoritesService] addServerFavorite for peerId: ${peerId}, type: ${type} -- SUCCESS`);
        }
        catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, "addServerFavorite");
            const errorMessage: string = `[FavoritesService] addServerFavorite for peerId: ${peerId}, type: ${type} -- FAILURE -- ${errorInfo.message}`;
            this.logger.error(errorMessage);
            throw errorInfo.error;
        }
    }

    /**
     * Create a new favorite as a FavoriteRB model
     * In addition to the info linked to the favorite itself (Id, position, type, peer Id), the peer object (Instance of Buble, Contact..)
     * is also is also stored in the favorite.
     * @param id - Id of the favorite.
     * @param peerId - Id of the peer linked to the favorite:  userId, bubbleId, botId, directoryId or office365Id
     * @param type - Type of the favorite peer: user/bubble/bot/directory/office365
     */
    private async createFavorite(id: string, peerId: string, type: FavoriteType): Promise<FavoriteRB | null> {
        try {
            const favorite: FavoriteRB = new FavoriteRB(id, peerId, type);

            // Get peer object, either Room or Bubble instance
            switch (type) {
                case FavoriteType.ROOM:
                    favorite.peer = this.bubbleService.getBubbleFromCache(peerId) as BubbleRB;
                    break;

                case FavoriteType.DIRECTORY:
                    // Try to find a Business or a Personal Directory Contact associated to this favorite
                    try {
                        let relatedContact: Contact = null;
                        relatedContact = await this.companyDirectoryService.getDirectoryContactById(peerId);
                        if (relatedContact) {
                            favorite.peer = relatedContact;
                        } else {
                            this.logger.error(`[FavoritesService] favorite ${id} (directory) will be deleted -- Unable to retrieve contact related to peer ${peerId}`);
                            return null;
                        }
                    }
                    catch (error) {
                        if (error.status === 403) {
                            // Special case where user does not have (or no more has) the PERSONAL_DIRECTORY feature flag in the profile.
                            // We will delete the favorite (but not the associated contact)
                            this.logger.error(`[FavoritesService] Favorite ${id} will be deleted -- User does not have the profile feature PERSONAL_DIRECTORY to read contact ${peerId}`);
                            this.deleteFavorite(id);
                            return null;
                        }
                    }
                    break;

                case FavoriteType.OFFICE365:
                    // Try to find a O365 Microsoft Azure Directory Contact associated to this favorite
                    if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MSO365_DIRECTORY_SEARCH)) {
                        // User does not have (or no more has) the MSO365_DIRECTORY_SEARCH feature flag in the profile.
                        // We will delete the favorite (but not the associated contact)
                        this.logger.error(`[FavoritesService] Favorite ${id} will be deleted -- User does not have the profile feature MSO365_DIRECTORY_SEARCH to read O365 contact ${peerId}`);
                        this.deleteFavorite(id);
                        return null;
                    }

                    try {
                        let relatedContact: Contact = null;
                        relatedContact = await this.office365AdService.getO365DirectoryContactById(peerId);
                        if (relatedContact) {
                            favorite.peer = relatedContact;
                        } else {
                            this.logger.error(`[FavoritesService] favorite ${id} (office365) will be deleted -- Unable to retrieve contact related to peer ${peerId}`);
                            return null;
                        }
                    }
                    catch (error) {
                        const errorInfo = await this.errorHelperService.getErrorInfo(error, "createFavorite");
                        this.logger.error(`[FavoritesService] createFavorite - getO365DirectoryContactById failure: ${errorInfo.message}`);
                        throw errorInfo.error;
                    }

                    break;

                case FavoriteType.OUTLOOK:
                    // Try to find a  Microsoft Outlook Contact associated to this favorite
                    if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MSO365_DIRECTORY_SEARCH)) {
                        // User does not have (or no more has) the MSO365_DIRECTORY_SEARCH feature flag in the profile.
                        // We will delete the favorite (but not the associated contact)
                        this.logger.error(`[FavoritesService] Favorite ${id} will be deleted -- User does not have the profile feature MSO365_DIRECTORY_SEARCH to read Outlook contact ${peerId}`);
                        this.deleteFavorite(id);
                        return null;
                    }

                    try {
                        let relatedContact: Contact = null;
                        relatedContact = await this.office365AdService.getOutlookContactById(peerId);
                        if (relatedContact) {
                            favorite.peer = relatedContact;
                        } else {
                            this.logger.error(`[FavoritesService] favorite ${id} (outlook) will be deleted -- Unable to retrieve contact related to peer ${peerId}`);
                            return null;
                        }
                    }
                    catch (error) {
                        const errorInfo = await this.errorHelperService.getErrorInfo(error, "createFavorite");
                        this.logger.error(`[FavoritesService] createFavorite - getOutlookContactById failure: ${errorInfo.message}`);
                        throw errorInfo.error;
                    }
                    break;

                default:
                    favorite.peer = await this.contactService.getContactByDBId(peerId);
                    break;
            }

            // No contact found, should remove the favorite. We do not do this for bubbles,
            // as during outage the bubbles usually dont work at the same time as contacts / favorites
            // Same for directory contact set as favorite: This last is not deleted if a problem was encountered
            // when retrieving the contact information. In the current session, this favorite will simply be not displayed.
            // if (type !== FavoriteType.ROOM && type !== FavoriteType.DIRECTORY && type !== FavoriteType.OFFICE365 && !favorite.peer) {
            if (type !== FavoriteType.ROOM && !this.isDirectoryFavorite(type) && !favorite.peer) {
                this.deleteFavorite(id);
                return null;
            }

            // Fetch eventual conversation
            // Not done for directory favorite (from personal/business/office365 directory) as there is never a conversation
            // associated with it as they are only contact cards
            if (!this.isDirectoryFavorite(type)) {
                const convId: string = favorite.peer.jid;
                const conv: Conversation = this.conversationService.getConversationFromCache(convId);
                if (conv) { conv.isFavorite = true; favorite.conv = conv as ConversationRB; }
            }
            return favorite;
        }
        catch (error) {
            this.logger.warn(`[FavoritesService] createFavorite id: ${id} peerId: ${peerId} type: ${type} -- FAILURE -- ${error.message}`);
            return null;
        }
    }

    // ------------------------------------------------------------
    //                 PUBLIC METHODS FOR UCAAS ONLY
    // ------------------------------------------------------------

    /**
     * This function is mainly used to manage directory contact
     * @param favorite - The favorite to update.
     * @param contact - The contact associated to the favorite.
     */
    public updateFavoriteContact(favorite: Favorite, contact: Contact): void {
        if (favorite) {
            const favoriteRB: FavoriteRB = favorite as FavoriteRB;
            favoriteRB.updateContact(contact);
            this.sendEvent('ON_FAVORITE_CONTACT_UPDATED', { favorite });
        }
    }

    /**
     * Search for a favorite associated with the specified contact
     * If found, the favorite data is updated with this contact.
     * Useful to update the displayed data (name, avatar) in the favorite list
     * This function is mainly used to manage personal directory contact
     * @param contact - The updated contact associated to the favorite.
     */
    public searchAndUpdateFavoriteContact(contact: Contact): void {
        // If a favorite is associated to the given contact, we update the dedicated field. Otherwise do nothing.
        const favorite: FavoriteRB = this.favorites.find((fav) => { return fav.peerId === contact.id; });
        if (favorite) {
            favorite.updateContact(contact);
            this.sendEvent('ON_FAVORITE_CONTACT_UPDATED', { favorite: favorite as Favorite });
        }
    }

    /**
     * Check if the specified peer (a user, bubble, direcoty contact...) is marked as favorite.
     * @param peerId - The updated contact associated to the favorite.
     */
    public isFavoriteConversation(peerId: string): boolean {
        const favorite: FavoriteRB = this.favorites.find((favoriteConv: any) => { return favoriteConv.peerId === peerId; });
        return (favorite !== undefined);
    }

    /**
     * Add or remove a favorite from a conversation
     * It will be checked whether there is a favorite with the contact linked to the conversation.
     * If so, the bookmark will be deleted; if not, it will be created.
     * @param conversation - The conversation that may be associated to the favorite.
     */
    public async toggleFavorite(conversation: ConversationRB) {
        try {
            const peerId: string = conversation.contact ? conversation.contact.dbId : conversation.bubble.dbId;
            const favorite: FavoriteRB = this.favorites.find((favoriteConv: any) => { return favoriteConv.peerId === peerId; });
            const peer: Contact | BubbleRB = conversation.contact ? conversation.contact : conversation.bubble;
            if (!favorite) {
                await this.addToFavorites(peer);
            }
            else {
                await this.deleteFavorite(favorite.id);
            }
        }
        catch (error) {
            // throw new Error(error);
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, "toggleFavorite");
            this.logger.error(`[FavoritesService] toggleFavorite failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Retrieves the conversation associated with a selected favorite
     * The selected favorite is specified via the peer Id, which is the id of the contact or bubble.
     * The selected favorite must have a conversation, so it can not be a favorite representing
     * a directory contact (business/personal/o365) for which there is no conversation.
     * @param peerId - Id of the peer marked as favorite
     */
    public async getFavoriteConversation(peerId: string): Promise<ConversationRB> {
        try {
            const favorite: FavoriteRB = this.favorites.find((favoriteConv: any) => { return favoriteConv.peerId === peerId; });
            let favConv: ConversationRB = null;
            if (favorite.peer instanceof Contact) {
                favConv = await this.conversationService.getOrCreateConversation(favorite.peer.jid, 'contact');
            }
            else if (favorite.peer instanceof BubbleRB) {
                favConv = await this.conversationService.getOrCreateConversation(favorite.peer.jid, 'room');
            } else {
                throw new Error("Error: peerRef not related to a contact or a bubble");
            }
            return favConv;
        }
        catch (error) {
            const errorMessage: string = `[FavoritesService] getFavoriteConversation -- FAILURE -- ${error.message}`;
            this.logger.error(errorMessage);
            throw new Error(errorMessage);
        }
    }

    // ------------------------------------------------------------
    //                      PUBLIC METHODS
    //     Used by internal Services/Components/... or by SDK
    // ------------------------------------------------------------

    /**
     * Get the list of my favorites.
     */
    public getFavorites(): Favorite[] {
        this.logger.info(`[FavoritesService] getFavorites -- Returns ${this.favorites.length}`);
        return this.favorites;
    }

    /**
     * Add the peer specified by its object to favorites list
     * The item is specified by its object instance (User or Bubble) and must be one of the following: A user, a bubble, a bot,
     * a directory contact (from Personal Contacts or Business Contacts directories) or a Microsoft Azure AD user/Microsoft O365 contact.
     * A ON_FAVORITE_CREATED event is sent afterwards at the completion of the operation to notify about the favorite creation.
     * @param peerRef - Reference of the peer to add to favorites list
     */
    public async addToFavorites(peerRef: User | Bubble): Promise<void> {
        let peerId: string = null;
        let type: FavoriteType;

        try {
            if (peerRef instanceof Contact) {
                const peerContact: Contact = peerRef;
                peerId = peerContact.dbId;

                if (peerContact.isBot) {
                    type = FavoriteType.BOT;
                }
                else if (peerContact.o365Flag) {
                    type = FavoriteType.OFFICE365;
                }
                else if (peerContact.outlookFlag) {
                    type = FavoriteType.OUTLOOK;
                }
                else if (peerContact.companyDirectoryFlag) {
                    type = FavoriteType.DIRECTORY;
                }
                else if (peerContact.personalDirectoryFlag) {
                    type = FavoriteType.DIRECTORY;
                }
                else {
                    // Considered as a User if no specific type found for the contact
                    type = FavoriteType.USER;
                }

            }
            else if (peerRef instanceof BubbleRB) {
                peerId = (peerRef).dbId;
                type = FavoriteType.ROOM;
            } else {
                throw new Error("Error: peerRef not related to a contact or a bubble");
            }

            if (!peerId) {
                throw new Error("Error: no related peer Id found");
            }

            await this.addServerFavorite(peerId, type);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "addToFavorites");
            this.logger.error(`[FavoritesService] addToFavorites failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Delete the favorite specified by its identifier.
     * The deletion is done on server side.
     * A ON_FAVORITE_DELETED event is sent afterwards at the completion of the operation to notify about the favorite deletion.
     * @param favoriteId - Favorite unique identifier
     */
    public async deleteFavorite(favoriteId: string): Promise<void> {
        try {
            if (!favoriteId) {
                throw new Error("Error: parameter 'id' is missing or null");
            }

            const url: string = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/favorites/${favoriteId}`;
            const headers: any = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw response; }
            this.logger.info(`[FavoritesService] deleteFavorite id: ${favoriteId} -- SUCCESS`);
        }
        catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, "deleteFavorite");
            this.logger.error(`[FavoritesService] deleteFavorite failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Delete the favorite specified by its identifier.
     * The deletion is done on server side.
     * A ON_FAVORITE_DELETED event is sent afterwards at the completion of the operation to notify about the favorite deletion.
     * @param peerRef - Reference of the peer to remove from favorites list
     */
    public async deleteFromFavorites(peerRef: User | Bubble | null): Promise<void> {
        let peerId: string = null;

        try {
            if (!peerRef) {
                throw new Error("Error: parameter 'peerRef' is missing or null");
            }

            if (peerRef instanceof Contact) {
                peerId = (peerRef).dbId;
            }
            else if (peerRef instanceof BubbleRB) {
                peerId = (peerRef).dbId;
            } else {
                throw new Error("Error: peerRef not related to a contact or a bubble");
            }

            if (!peerId) {
                throw new Error("Error: no related peer Id found");
            }

            // Find the favorite id associated to the peer id
            const favorite: Favorite = this.favorites.find((favorite: any) => { return favorite.peerId === peerId; });
            if (favorite) {
                await this.deleteFavorite(favorite.id);
            } else {
                throw new Error("Error: No related favorite found for the given peer");
            }
        }
        catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, "deleteFromFavorites");
            this.logger.error(`[FavoritesService] deleteFromFavorites failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Move a favorite in the favorites list.
     * The favorite specified by its object reference, is moved to the index specified in the parameter.
     * In addition, all existing favorites from this index will also have their positions automatically shifted by 1.
     * A ON_FAVORITES_ORDER_CHANGED event is sent afterwards at the completion of the operation to notify about the favorite's move.
     * @param favorite - Reference of the favorite to move in the favorites list.
     * @param position - The new position (starting from 0) of this favorite in the list.
     */
    public async moveFavoritePosition(favorite: Favorite, position: number): Promise<any> {
        try {
            const url: string = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/favorites/${favorite.id}?position=${position}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) { throw response; }
            this.logger.info(`[FavoritesService] moveFavoritePosition(${favorite.id}, ${position}) -- SUCCESS`);
        }
        catch (error) {
            const errorMessage: string = `[FavoritesService] moveFavoritePosition(${favorite.id}, ${position}) -- FAILURE -- ${error.message}`;
            this.logger.error(errorMessage);
            throw new Error(errorMessage);
        }
    }

}
